import React, { useState, useEffect, ChangeEvent } from 'react';
import 'bootstrap/dist/css/bootstrap.css';

type TimeEditorProps = {
  initialValue: number; // Initial value in seconds
  onChange: (updatedValue: number) => void; // Callback to notify parent of updates
};

const TimeEditor: React.FC<TimeEditorProps> = ({ initialValue, onChange }) => {
  // Local state to manage input as string
  const [inputValue, setInputValue] = useState<string>("00:00");
  const [internalSeconds, setInternalSeconds] = useState<number>(initialValue); // Internal time state

  // Synchronize local state with parent when initialValue changes
  useEffect(() => {
    setInternalSeconds(initialValue);
    setInputValue(formatTime(initialValue));
  }, [initialValue]);

  // Format seconds to "MM:SS" or "MMM:SS"
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  // Parse input string to total seconds
  const parseTime = (input: string): number => {
    const sanitizedInput = input.replace(/[^0-9:]/g, ''); // Allow only digits and colon
    const parts = sanitizedInput.split(':').map(Number);

    if (parts.length === 1) {
      // Single input like "90" → 90 minutes
      return parts[0] * 60;
    } else if (parts.length === 2) {
      // "M:SS" or "MM:SS" or "MMM:SS"
      const minutes = parts[0] || 0;
      const seconds = parts[1] || 0;
      return minutes * 60 + seconds;
    }
    return 0; // Default to 0 seconds if invalid
  };

  // Handle user input as string (no immediate processing)
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // Validate format while typing (allow numbers and ':')
    if (/^\d{0,3}(:\d{0,2})?$/.test(input)) {
      setInputValue(input); // Update displayed value without affecting seconds
    }
  };

  // Handle blur to finalize input and parse value
  const handleBlur = () => {
    const parsedSeconds = parseTime(inputValue); // Parse input string to seconds
    const formattedTime = formatTime(parsedSeconds); // Re-format input

    setInputValue(formattedTime); // Update input with formatted value
    setInternalSeconds(parsedSeconds); // Update internal time
    onChange(parsedSeconds); // Notify parent component of change
  };

  return (
    <input
      type="text"
      className="form-control"
      value={inputValue}
      onChange={handleInputChange} // Handle input changes locally
      onBlur={handleBlur} // Validate and format on blur
      placeholder="MM:SS" // Guide users with placeholder
    />
  );
};

export default TimeEditor;
